/**
 * Boot readiness store (spec: docs/designs/boot-readiness.md).
 *
 * An external store — not React context — because its writers live at
 * different depths of the provider tree (PortalSpacetimeProvider is OUTSIDE
 * SpacetimeDBProvider; BootstrapSubscriptions and BootstrappedApp are
 * inside), and the reader (BootOverlay) must not remount when providers do.
 *
 * `ready` is STICKY: once every signal has been observed, the session is
 * booted for good — later disconnects, token refreshes, or gate churn never
 * bring the full-screen preloader (or error card) back.
 */
import { useSyncExternalStore } from "react";

export type BootGate = {
  /** The one-shot inviteParams cookie parse has completed. */
  inviteParsed: boolean;
  /**
   * Signed-out visitor with no invite — held behind the overlay while the
   * login() redirect fires (never reveals).
   */
  anonymousHold: boolean;
};

type BootFlags = {
  tokenResolved: boolean;
  connected: boolean;
  viewsApplied: boolean;
  gate: BootGate;
  ready: boolean;
  connectionError: Error | null;
  progress: number;
  appliedViewCount: number;
};

const initialFlags = (): BootFlags => ({
  tokenResolved: false,
  connected: false,
  viewsApplied: false,
  gate: { inviteParsed: false, anonymousHold: false },
  ready: false,
  connectionError: null,
  progress: 0,
  appliedViewCount: 0,
});

let flags = initialFlags();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function recompute() {
  if (flags.ready) return; // sticky
  if (
    flags.tokenResolved &&
    flags.connected &&
    flags.viewsApplied &&
    flags.gate.inviteParsed &&
    !flags.gate.anonymousHold
  ) {
    flags.ready = true;
  }
}

function recordProgress(): void {
  if (flags.ready) return;
  flags.progress++;
  recompute();
  emit();
}

export function markTokenResolved(): void {
  if (flags.tokenResolved) return;
  flags.tokenResolved = true;
  recordProgress();
}

export function markConnected(): void {
  if (flags.connected) return;
  flags.connected = true;
  recordProgress();
}

export function markViewsApplied(): void {
  if (flags.viewsApplied) return;
  flags.viewsApplied = true;
  recordProgress();
}

export function setBootGate(gate: BootGate): void {
  if (
    flags.gate.inviteParsed === gate.inviteParsed &&
    flags.gate.anonymousHold === gate.anonymousHold
  )
    return;
  flags.gate = gate;
  recordProgress();
}

export function isBootReady(): boolean {
  return flags.ready;
}

/** Only new applied-view milestones count; subscription churn is not progress. */
export function markViewsProgress(appliedCount: number): void {
  if (flags.ready || appliedCount <= flags.appliedViewCount) return;
  flags.appliedViewCount = appliedCount;
  recordProgress();
}

/** Preserve the SDK diagnostic for the visible root recovery UI. */
export function setBootConnectionError(error: Error | null): void {
  if (flags.ready || flags.connectionError === error) return;
  const recovering = flags.connectionError !== null && error === null;
  flags.connectionError = error;
  if (recovering) recordProgress();
  else emit();
}

export function useBootConnectionError(): Error | null {
  return useSyncExternalStore(
    subscribeBootState,
    () => (flags.ready ? null : flags.connectionError),
    () => null,
  );
}

export function useBootProgress(): number {
  return useSyncExternalStore(
    subscribeBootState,
    () => flags.progress,
    () => 0,
  );
}

export function subscribeBootState(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** React subscription to the sticky ready flag. */
export function useBootReady(): boolean {
  return useSyncExternalStore(subscribeBootState, isBootReady, isBootReady);
}

/** Tests only — module state persists across renders by design. */
export function resetBootState(): void {
  flags = initialFlags();
  emit();
}
